import React from 'react';
import { useReveal } from '../utils/useReveal';
import SectionLayout from './SectionLayout';

const About: React.FC = () => {
  const { ref, isVisible } = useReveal();

  return (
    <>
      <style>{`
        .about-section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .about-section.revealed {
          opacity: 1;
          transform: translateY(0);
        }

        .about-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          margin-top: 60px;
        }

        .about-header-wrapper {
          display: flex;
          align-items: flex-start;
          margin-bottom: 40px;
        }

        .about-header-content {
          flex: 1;
        }

        .about-main-content {
          display: flex;
          align-items: flex-start;
          gap: 60px;
        }

        .about-main-content .section-description {
          flex: 1;
        }

        @media (max-width: 1024px) {
          .about-header-wrapper {
            flex-direction: column;
            align-items: center;
            gap: 40px;
          }

          .about-main-content {
            flex-direction: column;
            align-items: center;
          }
        }

        .about-item {
          padding: 40px;
          background: var(--card-2, rgba(255, 255, 255, 0.02));
          border: 1px solid var(--border, rgba(255, 255, 255, 0.08));
          border-radius: 20px;
          transition: all 0.3s ease;
        }

        .about-item:hover {
          background: var(--card, rgba(255, 255, 255, 0.03));
          border-color: var(--border-strong, rgba(255, 255, 255, 0.12));
          transform: translateY(-4px);
        }

        .about-item-title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 16px;
          color: var(--text, #ffffff);
        }

        .about-item-description {
          font-size: 16px;
          color: var(--muted-2, rgba(255, 255, 255, 0.6));
          line-height: 1.6;
        }

        @media (max-width: 1024px) {
          .about-content {
            grid-template-columns: 1fr;
            gap: 40px;
          }
        }

        @media (max-width: 768px) {
          .about-item {
            padding: 32px 24px;
          }
        }

        .metallic-wrap {
          position: relative;
          width: 260px;
          height: 260px;
          flex-shrink: 0;
        }

        .metallic-logo {
          width: 100%;
          height: 100%;
          margin-right: 100px;
          position: relative;
          z-index: 2;
          mix-blend-mode: luminosity;
          filter: brightness(1.2) contrast(1.1);
        }

        .metallic-paint {
          position: absolute;
          inset: 0;
          z-index: 3;
          background: linear-gradient(
            120deg,
            #4b5563 0%,
            #d1d5db 15%,
            #ffffff 25%,
            #e5e7eb 35%,
            #9ca3af 45%,
            #ffffff 50%,
            #9ca3af 55%,
            #e5e7eb 65%,
            #ffffff 75%,
            #d1d5db 85%,
            #4b5563 100%
          );
          background-size: 400% 400%;
          animation: metallicFlow 4s linear infinite;
          mix-blend-mode: overlay;
          opacity: 0.9;
          mask-image: url('/tricipta.svg');
          mask-repeat: no-repeat;
          mask-position: center;
          mask-size: contain;
          -webkit-mask-image: url('/tricipta.svg');
          -webkit-mask-repeat: no-repeat;
          -webkit-mask-position: center;
          -webkit-mask-size: contain;
        }

        .metallic-paint::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            45deg,
            transparent 30%,
            rgba(255, 255, 255, 0.8) 50%,
            transparent 70%
          );
          animation: metallicShine 3s ease-in-out infinite;
          mix-blend-mode: screen;
        }

        @keyframes metallicFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes metallicShine {
          0%, 100% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
          50% { transform: translateX(100%) translateY(100%) rotate(45deg); }
        }

        .metallic-wrap::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.1"/></svg>');
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 4;
        }

        .metallic-wrap::before {
          content: "";
          position: absolute;
          inset: -10px;
          background: radial-gradient(ellipse at center, rgba(255,255,255,0.15), transparent 70%);
          z-index: 1;
          animation: metallicGlow 2s ease-in-out infinite alternate;
        }

        @keyframes metallicGlow {
          0% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
      <SectionLayout id="tentang" maxWidth="2xl">
        <section 
          className={`about-section ${isVisible ? 'revealed' : ''}`} 
          ref={ref as React.RefObject<HTMLElement>}
        >
          <div className="about-header-wrapper">
            <div className="about-header-content">
              <div className="section-header">
                <h2 className="section-title">Tentang Kami</h2>
              </div>
              <p className="section-description">
                Kami adalah perusahaan teknologi yang berfokus pada solusi CRM omnichannel dan transformasi digital. Dengan produk unggulan Uidesk Omnichannel, Datakelola, dan WAgent, kami membantu bisnis meningkatkan customer experience dan efisiensi operasional.
              </p>
            </div>
            <div className="metallic-wrap">
              <img
                src="/tricipta.svg"
                alt="Logo"
                className="metallic-logo"
              />
              <div className="metallic-paint" />
            </div>
          </div>
        </section>
      </SectionLayout>
    </>
  );
};

export default About;

